import { BaseError } from 'make-error';
import { ReachAPI } from '../../ReachApi';

import { getPlayerUuid, getPreviewIds, PreviewIds } from '../../Signage/utility';

interface PlayerIdentity {
    playerUuid: string;
}

type PreviewIdentity = PreviewIds;

/**
 * Creates an instance representing an error that occurs when the Signage's identity cannot be
 * determined.
 */
export class MissingIdentity extends BaseError {
    constructor(message?: string) {
        super(message);
    }
}

function isPlayerIdentity(obj: unknown): obj is PlayerIdentity {
    return obj instanceof Object && 'playerUuid' in obj;
}

function isPreviewIdentity(obj: unknown): obj is PreviewIdentity {
    return obj instanceof Object && 'securityToken' in obj && 'displayId' in obj;
}

function getIdentity(): PlayerIdentity | PreviewIdentity {
    const playerUuid = getPlayerUuid();
    const previewIds = getPreviewIds();

    if (playerUuid !== null) {
        return {
            playerUuid,
        };
    } else if (previewIds !== null) {
        return previewIds;
    }

    throw new MissingIdentity('Signage does not have enough information to be identified.');
}

/**
 * Returns a populated {@link ReachAPI.Security} object for use with {@link ReachAPI} functions.
 * @throws {@link MissingIdentity}
 * Thrown if Signage does not have enough information to be identified.
 */
export function getSecurityIdentity(): ReachAPI.Security {
    const identity = getIdentity();
    let apiSecurity: ReachAPI.Security;

    if (isPlayerIdentity(identity)) {
        apiSecurity = new ReachAPI.Security(identity.playerUuid, undefined);
    } else if (isPreviewIdentity(identity)) {
        apiSecurity = new ReachAPI.Security(undefined, identity.securityToken);
    } else {
        throw new MissingIdentity("This shouldn't be possible");
    }

    return apiSecurity;
}
